import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  taxReportJ05, type TaxReport, type TaxReportPreview,
  type TaxReportType, type TaxReportCheck,
} from '@/api/taxReportJ05';
import { orgs } from '@/api/catalogs';
import { dpsSubmissions } from '@/api/dpsSubmissions';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import TaxReportJ05FormView from './TaxReportJ05FormView';
import TaxReportJ05Corrections from './TaxReportJ05Corrections';
import DatePopover from '@/components/DatePopover';
import { useDefaults } from '@/store/defaults';

// ч.1342: поданий розрахунок (сервер відмовляє в змінах, крім відхилених / помилки відправки).
const SUBMITTED_STATUSES = ['SENT', 'DELIVERED', 'ACCEPTED', 'REGISTERED', 'OK'];

/**
 * Редактор «Податкового розрахунку» (J0500110). Крок створення: юрособа +
 * період + тип + додатки. Далі — перегляд автозаповнення (агрегати + рядки
 * по фізособах з нарахувань ЗП) з можливістю ручної правки ключових сум і
 * вивантаження XML-пакета (основний + Д1 + 4ДФ) для подання через Кабінет ДПС.
 */

const now = new Date();
const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];
const TYPE_OPTIONS: { v: TaxReportType; l: string }[] = [
  { v: 'REPORTING', l: 'Звітний' },
  { v: 'REPORTING_NEW', l: 'Звітний новий' },
  { v: 'CORRECTING', l: 'Уточнюючий' },
];
const ALL_APPENDICES: { code: string; label: string }[] = [
  { code: 'D1', label: 'Додаток 1 (ЄСВ по працівниках)' },
  { code: '4DF', label: 'Додаток 4ДФ (ПДФО/ВЗ по працівниках)' },
  { code: 'D5', label: 'Додаток Д5 (трудові відносини: прийняття/звільнення/переведення/ЦПУ)' },
  { code: 'D2', label: 'Додаток Д2 (догляд за дитиною/особою — рядки вводяться вручну)' },
  { code: 'D3', label: 'Додаток Д3 (строкова військова служба — рядки вводяться вручну)' },
  { code: 'D6', label: 'Додаток Д6 (спецстаж — рядки вводяться вручну)' },
];

const money = (v: number) =>
  v.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function TaxReportJ05Editor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('payroll.manage');
  const defaultOrgId = useDefaults((st) => st.defaultOrgId);

  const [orgId, setOrgId] = useState('');
  const [reportType, setReportType] = useState<TaxReportType>('REPORTING');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));
  const [apps, setApps] = useState<string[]>(['D1', '4DF']);
  // ч.806: розрахунок за відокремлений підрозділ ('' = основний).
  const [placeId, setPlaceId] = useState('');
  // ч.1295: коригуючий — який поданий розрахунок за цей період виправляється.
  const [correctsId, setCorrectsId] = useState('');
  const isCorrection = reportType !== 'REPORTING';
  const [error, setError] = useState<string | null>(null);
  const [rep, setRep] = useState<TaxReport | null>(null);
  const [preview, setPreview] = useState<TaxReportPreview | null>(null);
  const [subStatus, setSubStatus] = useState<string | null>(null);
  const [check, setCheck] = useState<TaxReportCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Iнкремент → форма-бланк перезавантажується (дата подання HFILL у формах
  // авто-походить від document_date — після зміни дати вгорі оновлюємо).
  const [formKey, setFormKey] = useState(0);

  const effOrgId = orgId
    || (availableOrgs.some((o) => o.id === defaultOrgId) ? defaultOrgId : '')
    || availableOrgs[0]?.id || '';
  // ФОП (наказ №243): з II кв. 2026 — окрема КВАРТАЛЬНА форма F0500111
  // (додатки лише Д1/4ДФ/Д5). Юрособи — місячна J0500111.
  const isFop = orgList.data?.items
    .find((o) => o.id === effOrgId)?.entity_type === 'FOP';
  // ч.806: місця діяльності (відокремлені підрозділи) юрособи.
  const places = isFop ? [] : (orgList.data?.items.find((o) => o.id === effOrgId)?.activity_places ?? []);

  // Режим перегляду існуючого розрахунку.
  useEffect(() => {
    if (isNew) return;
    taxReportJ05.get(id!).then((r) => {
      setRep(r);
      dpsSubmissions.list({
        source_type: 'doc.tax_reports', source_id: id!, limit: 1,
      }).then((s) => setSubStatus(s.items[0]?.status ?? null)).catch(() => {});
      return taxReportJ05.preview(id!).then(setPreview);
    }).catch(() => setError('Розрахунок не знайдено'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function create(manualOnly: boolean) {
    setError(null);
    if (!effOrgId) { setError('Оберіть організацію'); return; }
    if (!apps.length) { setError('Оберіть хоча б один додаток'); return; }
    try {
      const r = await taxReportJ05.create({
        organization_id: effOrgId, report_type: reportType,
        // ФОП (наказ №243, з II кв. 2026): КВАРТАЛЬНА форма F0500111 —
        // period_kind=QUARTER; юрособи — місячна.
        ...(isFop
          ? { period_kind: 'QUARTER', period_year: year, period_quarter: quarter }
          : { period_kind: 'MONTH', period_year: year, period_month: month }),
        // ч.806: за відокремлений підрозділ — лише 4ДФ (Д1/Д5 — в основному).
        appendices: placeId ? ['4DF'] : isFop
          ? apps.filter((a) => ['D1', '4DF', 'D5'].includes(a)) : apps,
        manual_only: manualOnly || isCorrection,
        activity_place_id: placeId || null,
        corrects_report_id: isCorrection ? (correctsId || null) : null,
      });
      const pv = await taxReportJ05.preview(r.id);
      setRep(r);
      setPreview(pv);
      navigate(`/tax-reports-j05/${r.id}`, { replace: true });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Помилка створення');
    }
  }

  async function saveDocDate(dateYmd: string) {
    if (!rep || !dateYmd) return;
    try {
      // Дата подання (D_FILL/HFILL основного і додатків) — авто-походить від
      // document_date; після зміни перезавантажуємо форму-бланк (formKey).
      const iso = `${dateYmd}T00:00:00Z`;
      const upd = await taxReportJ05.patch(rep.id, { document_date: iso });
      setRep(upd);
      setCheck(null);
      setFormKey((k) => k + 1);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Помилка зміни дати');
    }
  }

  async function runCheck() {
    if (!rep) return;
    setChecking(true);
    try {
      setCheck(await taxReportJ05.check(rep.id));
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Помилка перевірки');
    } finally {
      setChecking(false);
    }
  }

  // «Зберегти» — чернетка з'являється у Кабінеті ДПС (ідемпотентно).
  /** Завантажити XML-файли пакета (основний + додатки). Потрібно, коли
   *  API ДПС повертає сухий код помилки — файли можна залити у веб-кабінет
   *  ДПС і побачити детальну причину (запит власника 2026-08-18). */
  async function downloadXml() {
    try {
      const { files } = await taxReportJ05.files(id!);
      for (const f of files) {
        const bytes = Uint8Array.from(atob(f.xml_base64), (c) => c.charCodeAt(0));
        const url = URL.createObjectURL(
          new Blob([bytes], { type: 'application/xml' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = f.fname;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      }
    } catch (e: any) {
      window.alert(e?.response?.data?.detail ?? 'Не вдалося сформувати XML');
    }
  }

  // Подані статуси = розрахунок уже пішов у ДПС (чернетка у Кабінеті —
  // ще ні). Видалення дозволене лише до відправки.
  const canDelete = !!rep && (!subStatus || subStatus === 'DRAFT');

  async function removeReport() {
    if (!rep) return;
    if (!window.confirm('Видалити розрахунок?')) return;
    setDeleting(true);
    try {
      await taxReportJ05.remove(rep.id);
      navigate('/tax-reports-j05');
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setDeleting(false);
    }
  }

  async function saveDraft() {
    if (!rep) return;
    setSaving(true);
    try {
      await taxReportJ05.saveToCabinet(rep.id);
      if (!subStatus) setSubStatus('DRAFT');
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Помилка збереження');
    } finally {
      setSaving(false);
    }
  }

  function toggleApp(code: string) {
    setApps((a) => a.includes(code) ? a.filter((x) => x !== code) : [...a, code]);
  }

  // ч.1393 (рішення власника): сторінка створення — як «Нова декларація з ПДВ»: вузька картка,
  // поля одне під одним, додатки списком, «до каталогу» Кабінету ДПС.
  if (isNew) {
    const appList = ALL_APPENDICES.filter((a) => !isFop || ['D1', '4DF', 'D5'].includes(a.code));
    return (
      <div className="card" style={{ maxWidth: 620 }}>
        <div className="toolbar">
          <h2 style={{ margin: 0 }}>Новий податковий розрахунок</h2>
          <button style={{ marginLeft: 'auto' }}
            onClick={() => navigate('/dps-cabinet/new')}>до каталогу</button>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: 0 }}>
          Суми і таблиці заповняться з нарахувань зарплати — далі перевірите бланк і збережете
          розрахунок у Кабінет ДПС, а підпис і відправка — там.
        </p>
        <div className="field">
          <label>Організація *</label>
          <select value={effOrgId} onChange={(e) => { setOrgId(e.target.value); setPlaceId(''); setCorrectsId(''); }}>
            {availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Тип</label>
          <select value={reportType}
            onChange={(e) => { setReportType(e.target.value as TaxReportType); setCorrectsId(''); }}>
            {TYPE_OPTIONS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 9' }}>
            <label>{isFop ? 'Звітний квартал * (ФОП — квартальна форма)' : 'Звітний місяць *'}</label>
            {isFop ? (
              <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
                {[1, 2, 3, 4].map((q) => (
                  <option key={q} value={q}>{['I', 'II', 'III', 'IV'][q - 1]} квартал</option>
                ))}
              </select>
            ) : (
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m.toLowerCase()}</option>)}
              </select>
            )}
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Рік</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {Array.from({ length: 5 }, (_, i) => now.getFullYear() - 3 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        {/* ч.806: розрахунок «за відокремлений підрозділ» — працівники місця,
            лише 4ДФ; 032/033 — КАТОТТГ і назва місця з картки організації. */}
        {places.length > 0 && (
          <div className="field">
            <label>За відокремлений підрозділ</label>
            <select value={placeId} onChange={(e) => setPlaceId(e.target.value)}>
              <option value="">— основний розрахунок (4ДФ без працівників підрозділів)</option>
              {places.map((p) => (
                <option key={p.id ?? p.name} value={p.id ?? ''}>{p.name}{p.katottg ? ` · ${p.katottg}` : ''}</option>
              ))}
            </select>
          </div>
        )}
        {isCorrection && (
          <CorrectsPicker orgId={effOrgId} year={year} month={isFop ? null : month}
            quarter={isFop ? quarter : null} value={correctsId} onChange={setCorrectsId}
            kind={reportType === 'CORRECTING' ? 'уточнюючий' : 'звітний новий'} />
        )}
        <div style={{ marginTop: '0.25rem' }}>
          <label style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
            Додатки{placeId ? ' (за підрозділ — лише 4ДФ)' : ''}
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: 4 }}>
            {/* ФОП-форма (F0500111): лише Д1/4ДФ/Д5 — Д2/Д3 у F-серії
                відсутні, Д6 поки не підтримано. */}
            {appList.map((a) => (
              <label key={a.code} style={{ display: 'flex', gap: '0.4rem',
                alignItems: 'center', cursor: 'pointer', fontSize: '0.88rem' }}>
                <input type="checkbox" style={{ width: 'auto' }}
                  checked={placeId ? a.code === '4DF' : apps.includes(a.code)}
                  disabled={!!placeId}
                  onChange={() => toggleApp(a.code)} />
                {a.label}
              </label>
            ))}
          </div>
          <p style={{ color: 'var(--muted)', fontSize: '0.8rem', margin: '0.4rem 0 0.6rem' }}>
            Рядки Д2, Д3 і Д6 вводяться вручну на сторінці розрахунку.
          </p>
        </div>
        {error && (
          <div style={{ color: 'var(--badge-red-fg)', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {isCorrection ? (
            <button className="primary" onClick={() => create(true)} disabled={!effOrgId}
              title="Коригуючий містить лише виправлення: нарахування періоду у нього не тягнуться — рядки беруться з поданого розрахунку або вводяться вручну">
              створити {reportType === 'CORRECTING' ? 'уточнюючий' : 'звітний новий'}
            </button>
          ) : (
            <>
              <button className="primary" onClick={() => create(false)} disabled={!effOrgId}>
                створити і заповнити з нарахувань
              </button>
              <button onClick={() => create(true)} disabled={!effOrgId}
                title="Створити порожній розрахунок — заповнено лише реквізити шапки, суми і таблиці бухгалтер вбиває вручну">
                створити без нарахувань
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий податковий розрахунок'
            : (rep?.report_type === 'CORRECTING' ? 'Уточнюючий податковий розрахунок № '
              : rep?.report_type === 'REPORTING_NEW' ? 'Звітний новий податковий розрахунок № '
              : 'Податковий розрахунок № ') + `${rep?.report_ordinal ?? ''}`
              // ч.1393: квартальний розрахунок ФОП — «за IV квартал 2026», а не «за /2026».
              + (!rep ? ''
                : rep.period_month ? ` за ${rep.period_month}/${rep.period_year}`
                : ` за ${['I', 'II', 'III', 'IV'][(rep.period_quarter ?? 1) - 1]} квартал ${rep.period_year}`)
              + (rep?.activity_place_name ? ` — за підрозділ «${rep.activity_place_name}»` : '')}
        </h2>
        {rep && (
          <>
            <button style={{ marginLeft: 'auto' }}
              onClick={runCheck} disabled={checking}>
              {checking ? '…' : 'перевірити'}
            </button>
            <button onClick={downloadXml}
              title="Завантажити XML-файли пакета (можна залити у веб-кабінет ДПС і побачити детальні помилки)">
              завантажити XML
            </button>
            <button className="primary" onClick={saveDraft} disabled={saving}
              title="Зберегти чернетку розрахунку у Кабінеті ДПС">
              {saving ? '…' : 'зберегти'}
            </button>
            {/* Видалити можна, поки розрахунок НЕ пішов у ДПС: після
                подання це обліковий факт (є квитанції, номер у періоді
                зайнятий) — виправляють уточнюючим. Сервер тримає той
                самий гард. */}
            <button className="danger" onClick={removeReport}
              disabled={deleting || !canDelete}
              title={canDelete
                ? 'Видалити розрахунок'
                : 'Розрахунок уже подано до ДПС — видалення недоступне'}>
              {deleting ? '…' : 'видалити'}
            </button>
          </>
        )}
        <button style={rep ? undefined : { marginLeft: 'auto' }}
          onClick={() => navigate('/dps-cabinet')}>до Кабінету</button>
      </div>

      {error && (
        <div style={{ background: 'var(--badge-red-bg)', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>{error}</div>
      )}

      {/* Перегляд автозаповнення */}
      {preview && rep && (
        <div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap',
            alignItems: 'center', marginBottom: '0.75rem' }}>
            <b>{preview.org.name}</b>
            <span style={{ color: 'var(--muted)' }}>ЄДРПОУ {preview.org.edrpou}</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4,
              fontSize: '0.82rem', color: 'var(--muted)' }}
              title="Дата подання (HFILL) в основному розрахунку і всіх додатках">
              Дата подання
              <div style={{ width: 130 }}>
                <DatePopover value={rep.document_date?.slice(0, 10) ?? ''}
                  onChange={saveDocDate}
                  disabled={SUBMITTED_STATUSES.includes(rep.submission_status ?? '')} />
              </div>
            </label>
          </div>
          {SUBMITTED_STATUSES.includes(rep.submission_status ?? '') && (
            <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: 6,
              background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)',
              border: '1px solid var(--badge-amber-bd)', fontSize: '0.85rem' }}>
              Розрахунок подано до ДПС — змінити його не можна. Для виправлення створіть
              уточнюючий розрахунок.
            </div>
          )}

          {/* Результати перевірки */}
          {check && (
            <div style={{ marginBottom: '1rem' }}>
              {check.errors.length === 0 && check.warnings.length === 0 && (
                <div style={{ background: 'var(--badge-green-bg)', border: '1px solid #bbf7d0',
                  color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
                  ✓ Помилок не знайдено — розрахунок готовий до подання.
                </div>
              )}
              {check.errors.length > 0 && (
                <div style={{ background: 'var(--badge-red-bg)', border: '1px solid #fecaca',
                  color: 'var(--badge-red-fg)', padding: '0.5rem 0.75rem', borderRadius: 6,
                  marginBottom: '0.5rem' }}>
                  <b>Помилки ({check.errors.length}) — заважають поданню:</b>
                  <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>
                    {check.errors.map((e, i) => (
                      <li key={i} style={{ fontSize: '0.85rem' }}>
                        [{e.form === 'MAIN' ? 'Основний' : e.form}] {e.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {check.warnings.length > 0 && (
                <div style={{ background: 'var(--badge-amber-bg)', border: '1px solid #fde68a',
                  color: 'var(--badge-amber-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
                  <b>Попередження ({check.warnings.length}) — перевірте:</b>
                  <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>
                    {check.warnings.map((w, i) => (
                      <li key={i} style={{ fontSize: '0.85rem' }}>
                        [{w.form === 'MAIN' ? 'Основний' : w.form}] {w.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Статус у Кабінеті ДПС */}
          <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)',
            color: 'var(--badge-blue-fg)', padding: '0.5rem 0.75rem', borderRadius: 6,
            marginBottom: '1rem', display: 'flex', gap: '0.5rem',
            alignItems: 'center', flexWrap: 'wrap' }}>
            <span>
              {subStatus === 'ACCEPTED' ? '✓ прийнято ДПС (квитанція №2)'
                : subStatus === 'REJECTED' ? '✗ відхилено ДПС'
                : subStatus === 'DELIVERED' ? '✓ доставлено (кв.1), очікуємо кв.2'
                : subStatus === 'SENT' ? '→ відправлено у ДПС'
                : subStatus === 'SIGNED' ? 'підписано — лишилося відправити у Кабінеті ДПС'
                : subStatus ? 'збережено чернеткою у Кабінеті ДПС — підпишіть і відправте там'
                : 'ще не збережено у Кабінеті ДПС — натисніть «зберегти» вгорі'}
            </span>
            <a style={{ cursor: 'pointer', marginLeft: 'auto' }}
              onClick={() => navigate('/dps-cabinet')}>Кабінет ДПС →</a>
          </div>

          {/* ч.1295: коригуючий — панель виправлень (рядки з поданого розрахунку). */}
          {rep.report_type !== 'REPORTING' && (
            <TaxReportJ05Corrections rep={rep} onApplied={() => { setCheck(null); setFormKey((k) => k + 1); }} />
          )}

          {/* Підсумки (read-only, авто з нарахувань) — компактні бейджі; у коригуючому
              нарахувань немає — не показуємо. */}
          {rep.report_type === 'REPORTING' && <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem',
            marginBottom: '0.75rem' }}>
            {([
              ['Осіб', String(preview.totals.insured_count)],
              ['Дохід', money(preview.totals.gross)],
              ['База ЄСВ', money(preview.totals.esv_base)],
              ['ЄСВ', money(preview.totals.esv)],
              ['ПДФО', money(preview.totals.pdfo)],
              ['ВЗ', money(preview.totals.wz)],
            ] as [string, string][]).map(([label, val]) => (
              <span key={label} style={{ display: 'inline-flex', gap: 5,
                alignItems: 'baseline', border: '1px solid var(--border)',
                borderRadius: 999, padding: '0.15rem 0.6rem',
                fontSize: '0.78rem', background: 'var(--bg-soft-2)' }}>
                <span style={{ color: 'var(--muted)' }}>{label}</span>
                <span style={{ fontWeight: 600 }}>{val}</span>
              </span>
            ))}
          </div>}

          {/* Форми пакета як бланк: рядки з офіційними назвами, редаговані.
              key=formKey → форма перезавантажується після зміни дати подання. */}
          <TaxReportJ05FormView key={formKey} repId={rep.id} errors={check?.errors} />

          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginTop: '0.75rem' }}>
            {rep.report_type !== 'REPORTING'
              ? 'Коригуючий розрахунок містить лише виправлення: у бланку вище — рядки на '
                + 'виключення (ознака 1) і введення (ознака 0) та сумові рядки Д1 (КТН 2/3); '
                + 'рядки 4.1/6.1 основної частини рахуються з них автоматично. Пакет підпишіть і '
                + 'подайте через Кабінет ДПС.'
              : 'Форми заповнено автоматично з проведених нарахувань ЗП і кадрових наказів.'}
            {rep.report_type === 'REPORTING' && ' '}{rep.report_type === 'REPORTING' && ` Будь-який рядок чи клітинку можна виправити вручну —
            правка перекриває авто у XML. Пакет підпишіть і подайте через
            Кабінет ДПС — там він іде одним поданням (пов'язаний LINKED_DOCS).`}
          </p>
        </div>
      )}
    </div>
  );
}


/** ч.1295: вибір поданого розрахунку за період, який виправляє коригуючий. */
function CorrectsPicker({ orgId, year, month, quarter, value, onChange, kind }: {
  orgId: string; year: number; month: number | null; quarter: number | null;
  value: string; onChange: (v: string) => void; kind: string;
}) {
  const q = useQuery({
    queryKey: ['tax-reports-j05', 'sent-for-period', orgId, year, month, quarter],
    queryFn: () => taxReportJ05.list({ organization_id: orgId, period_year: year,
      period_month: month ?? undefined, limit: 100 }),
    enabled: !!orgId,
  });
  const sent = (q.data?.items ?? []).filter((r) =>
    ['SENT', 'DELIVERED', 'ACCEPTED'].includes(r.submission_status ?? '')
    && (quarter == null || r.period_quarter === quarter));
  return (
    <div style={{ marginTop: '0.75rem', maxWidth: 640 }}>
      <div className="field">
        <label>Поданий розрахунок, що виправляється</label>
        <select value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{sent.length ? '— оберіть —' : '— поданих за цей період у системі немає —'}</option>
          {sent.map((r) => (
            <option key={r.id} value={r.id}>
              № {r.report_ordinal} від {new Date(r.document_date).toLocaleDateString('uk-UA')}
              {r.report_type === 'REPORTING' ? '' : ' · коригуючий'}
            </option>
          ))}
        </select>
      </div>
      <div style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
        {kind === 'уточнюючий'
          ? 'Уточнюючий подають після граничного строку подання за період.'
          : 'Звітний новий подають до закінчення граничного строку подання за період.'}
        {' '}Розрахунок міститиме лише виправлення: рядки з поданого на виключення (ознака 1) і
        правильні на введення (ознака 0), у Д1 — суми донарахованого / зменшеного ЄСВ (КТН 2/3).
        Звітні рядки 1–3 і чисельність не повторюються; додаток без виправлень у пакет не йде.
      </div>
    </div>
  );
}
